import { useColors } from '@/hooks/useColors'
import { SwatchesProps } from './types'
import { FC, useMemo, useState } from 'react'
import { Skeleton, Swatch, Text } from '@lounge-fe/ui-kit'
import { normaliseProductColor } from '@lounge-fe/ui-kit/src/utilities/normaliseProductColor'
import { useTranslation } from 'react-i18next'
import { Reviews } from '../Reviews'

type SwatchWithUrl = Swatch & { url?: string }

const Swatches: FC<SwatchesProps> = ({ selectedId, swatches }) => {
  const { colors: availableColors, hasLoaded } = useColors()
  const [color, setColor] = useState<string | undefined>(
    swatches.find((swatch) => swatch.id === selectedId)?.color,
  )

  const validSwatches = useMemo(() => {
    const getHexes = (swatch: SwatchWithUrl) => {
      let hexes: string[] = []

      if (swatch.color && availableColors) {
        const normalised = normaliseProductColor(swatch.color)
        hexes = availableColors[normalised]
      }

      if (swatch.canonical_color && !hexes?.length && availableColors) {
        const normalised = normaliseProductColor(swatch.canonical_color)
        hexes = availableColors[normalised] ?? []
      }

      // Fallback if no color or canonical_color is available
      if (!hexes?.length) {
        hexes = []
      }

      return {
        ...swatch,
        hexes,
        url: swatch.url ?? '#',
      }
    }

    return swatches.map(getHexes).filter((swatch) => swatch.hexes?.length)
  }, [swatches, availableColors])

  return (
    <div>
      <Text variant="title-six" className="mb-4">
        <span className="text-[#888888]">{color}</span>
      </Text>
      <div className="flex h-auto items-center flex-wrap gap-2">
        {hasLoaded ? (
          validSwatches.map((swatch) => (
            <Swatch
              as="a"
              href={swatch.url}
              key={swatch.id}
              swatch={swatch}
              hexes={swatch.hexes}
              isSelected={swatch.id === selectedId}
              onMouseEnter={() => setColor(swatch.color)}
            />
          ))
        ) : (
          <Skeleton className="flex flex-wrap gap-2">
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton.Block key={index} className="w-6 h-6" />
            ))}
          </Skeleton>
        )}
      </div>
    </div>
  )
}

export default Swatches
